import * as THREE from 'three';
import { FishBatch, facesRight, fishAtlas, type FishDraw } from '../fishBatch';
import type { FogUniforms } from '../skin';
import { pickNearest } from '../pick';
import { hash3, windowShare } from '../fishSwim';
import { inView } from '../persistence';
import { HONU_STROKE, frameQuad, strokeHoldS } from '../turtles';
import { HONU_CARAPACE_PX, HONU_FRAMES, HONU_SPRITE } from '../../ui/data/honuSprites';
import { HAWKSBILL_CARAPACE_PX, HAWKSBILL_SPRITES } from '../../ui/data/hawksbillSprites';
import { MONK_SEAL_SPRITES } from '../../ui/data/monkSealSprites';
import { TIGER_SHARK_SPRITES } from '../../ui/data/tigerSharkSprites';
import { REEF_FISH_SPRITES } from '../../ui/data/reefFishSprites';
import { WHALE_SPRITES } from '../../ui/data/whaleSprites';
import { WHALE_DRAW, strokeFrame, strokeStart } from '../whales';
import type { Sprite } from '../../ui/data/spriteTypes';
import type { FishPose, PosableFish } from '../fishSchools';
import { FISH_KINDS } from '../fishKinds';
import type { FishSpeciesId } from '../../sim/fish';
import { sentenceName, speciesOf } from '../../sim/species';
import { SPECIES_VISITS, type VisitorSpecies } from '../../sim/visits';
import { MANTA } from '../../sim/reefResidents';
import { clockLabel } from '../../ui/observation';
import type { Caster } from './shadows';

/**
 * The honu and the monk seals visiting a habitat's scene (M145, the user's word: "do 4 but make the seal odds much
 * higher, like 20%. also for all the sea creatures, you should be able to see them in other habitats as well if they
 * travel out there"). A scene shows one representative place at true counts, where a honu is less than one and a seal
 * far less (the Reef's hectare 0.39 honu and 0.003 seals, MEASURED on a new world): so each comes on visits of a few days
 * (since M266, `visitOn`; each day's own draw before), as many of its days there as its odds (`VisitOdds`, the data's),
 * the same however often the scene is opened, and one that comes
 * swims back and forth across the scene's view, near the bottom where the scene has one, facing the way the camera
 * sees it swim (M142), a honu stroking its two frames as it goes (M143). The hawksbill (M196) comes to the Reef as the
 * honu does, by its hectare's count, stroking its own two frames.
 *
 * Each a rendering choice (JUDGED): `spanM` the lane's half-length either side of the view's middle, at most
 * `seenShare` of what the home view sees across there; `riseS` the seconds its height takes to follow the floor's; `aboveM` its
 * height over the bottom, a body length's share; `lengthsPerS` its pace, body lengths a second; `fadeS` its fading in.
 */
export const VISITOR_DRAW = { spanM: 9, seenShare: 0.8, aboveShare: 0.6, lengthsPerS: 0.35, fadeS: 2, riseS: 1, fishTimes: 1.75 } as const;

/**
 * A visitor's passes (M266, JUDGED): its lane run past what the home view sees across by a share `beyond` more, and its
 * own drawn length, so it comes in, crosses and goes out of sight before it turns. One no longer wanted swims on until it
 * is out of view, for at most `leaveS` seconds and half the seconds between the day's relays (a day's at 1×, about one
 * at speed), then fades where it is: so it is gone within the day it left on (M266's review: swimming its whole lane,
 * the gone piled up, 50 honu at once at 128×, and a tiger shark drawn on 11% of the time at 16× against its 0.4%). One
 * comes in swimming only while the relays are more than `swimInS` apart (4× and slower: at 16× and on, a relay a second,
 * a visit of days lasts a second or two, too short to swim into view, and it fades in where its round has it, as before).
 * A relay sooner than `leastRelayS` after the last (a tag's hand-over relays the same day) is not counted as the day's;
 * one leaving fades over no less than `leastFadeS` (review 31: both JUDGED, a blink's length). One coming in whose way in is
 * seen is moved out along its lane by `entryStep` of its round at a time (JUDGED: a few hundredths of a meter on a lane
 * of tens) until none of it is.
 */
export const VISITOR_PASS = { beyond: 1.15, leaveS: 6, swimInS: 1.2, leastRelayS: 0.5, leastFadeS: 0.25, entryStep: 0.002 } as const;

/**
 * The hawksbill's stroke (M196): its two frames swapped as the honu's are (`strokeHoldS`), at the honu's way a stroke
 * in body lengths (`HONU_STROKE.lengthsPerStroke`, Davenport's young green turtles; JUDGED, no hawksbill's stroke
 * read), its body `bodyPx`, the sprites' snout at column 0 to its shell's rear tip at 149 (MEASURED off the sprites).
 */
const HAWKSBILL_STROKE = { bodyPx: 150 } as const;

/**
 * The humpback over the Reef (M210, the user's word: "show the humpback entering the reef when its appropriate"): the
 * day's whales there by its visits' count (`speciesVisits`), each drawn at its true length on a lane of its own across
 * the home view. Each a rendering choice (JUDGED unless said):
 * - `fitShare`: its lane as far from the home view's eye as makes its length 0.55 of the width of the part of the
 *   screen the panels leave, read off the camera's own projection (M210's review: of the canvas's whole, it was 1.25
 *   times the gap between a computer's panels). Its lane is then 30, 23 and 11 m off on a phone held upright, a computer
 *   and a phone on its side, its length's share of the canvas under `NEAR_FADE.from` (0.75, where a sprite filling the
 *   screen fades). The user asked whether farther back would cut it less ("would making them a little further back in
 *   the screen reduce how much they're cut?"): a little, 0.28 cutting 0%, 17% and 72% of its drawn height against
 *   0.55's 18%, 27% and 61%, at 57, 45 and 21 m, the first two past where the water hides it (`seenM` 37.6 m;
 *   MEASURED in `tests/humpbackReef.test.ts`).
 * - `leastM`: never nearer than 8 m, the reef's front row of outcrops.
 * - `easeS`: its lane's distance eased to a new screen's over 2 seconds, a fading's (M210's review: a panel opened
 *   moved it 7.5 m in a frame).
 * - `laneShare`: the whales' lanes spread over 0.3 of their length, each within 0.15 of it nearer or farther, so two of
 *   a day pass apart; never nearer than `leastM` for it.
 * - Its lane runs on past the view's edges by half its length, so it passes across the view and turns out of sight (a
 *   lane within the view, as the others', would hold a whale over half the view's width turning back every few seconds).
 * - `floorGapM`: its lowest drawn row 0.3 m over the floor's highest within its length either side (`floorUnder`), low
 *   because the home view looks down on the reef and a whale higher is cut more by its top edge; `surfaceM`: never
 *   nearer the surface than 0.5 m.
 * - Its pace and its stroke the open water's (`WHALE_DRAW`'s `lengthsPerS`, cited there, and `strokeStart`).
 */
export const WHALE_VISIT = { fitShare: 0.55, leastM: 8, floorGapM: 0.3, surfaceM: 0.5, laneShare: 0.3, easeS: 2 } as const;

/**
 * One visitor the day brings: its kind, its stage, its length, m (snout to tail; a honu's carapace for a honu). A
 * species' visitor (M147, `src/sim/visits.ts`) holds a slot among its kind's that day, and is the one there before its
 * slot's swap or the one that comes in its place after it (`crossingOf`): `after`, and the swap's hour and the habitat
 * it goes back to (or comes from); a reef fish drawn `fishTimes` its length, as the Reef draws its fish
 * (`REEF_SCENE.fish.drawnTimes`, JUDGED there), so a fish seen on both is seen the same size.
 */
export interface Visitor {
  kind: 'honu' | 'honuea' | 'seal' | 'tigerShark' | 'whitetip' | 'manta' | VisitorSpecies;
  stage: 'adult' | 'young' | 'juvenile' | 'pup' | 'terminal';
  lengthM: number;
  slot?: { day: number; index: number; after: boolean; hour: number; home: string };
  /** A tagged animal's (M203): its tag's id and name, drawn where its tag tells it is today (`tagPlaceOn`). */
  tag?: { id: number; name: string };
  /** A tagged one's: the day's visitor of its kind it stands in for (its `visitorId`), whose place as drawn it takes (M203's review: the one tapped and tagged swapped for another at another point of the scene). */
  from?: string;
  /** Its age class where the drawing drew it from its population's (a turtle's, a seal's, a shark's), so a tag of it is of its class (M203's review: its size changed as it was tagged). */
  cls?: number;
  /** A manta's (M242): whether a female, and the hour it comes to the reef (`mantaArrivalHour`), gone at sunset (`mantaHere`). */
  female?: boolean;
  arriveHour?: number;
  /** Its visit's first day (M266, `visitOn`): one visit's animal the same all its stay, the next one's another. */
  visit?: number;
}

/**
 * A visitor's own name among a scene's: a honu's or a seal's its kind, a species' its kind, day, slot and side of its
 * swap (the day's, so the next day's fish in the same slot is not taken for it: M147's review).
 */
export const visitorId = (v: Visitor): string => (v.tag ? `tag:${v.tag.id}` : v.slot ? `${v.kind}:${v.slot.day}:${v.slot.index}:${v.slot.after ? 1 : 0}` : v.visit !== undefined ? `${v.kind}@${v.visit}` : v.kind);

/** Whether a visitor's kind is a reef fish drawn as the reef's are (M147). */
const fishKindOf = (kind: Visitor['kind']): (typeof FISH_KINDS)[number] | undefined => FISH_KINDS.find((k) => k.species === (kind as FishSpeciesId));

/** A visitor's length as drawn, m: a reef fish's `VISITOR_DRAW.fishTimes` its own (M256), any other's its own. */
const drawnLengthM = (v: Visitor): number => v.lengthM * (fishKindOf(v.kind) ? VISITOR_DRAW.fishTimes : 1);

/** A visitor's lane among the scene's, for its draws: the honu's 0 and the seal's 1, as M145 drew them; a species' slot its own after them. */
function laneOf(id: string): number {
  // Its kind's lane, whatever visit it is on (M266).
  const kind = id.replace(/@\d+$/, '');
  if (kind === 'honu') return 0;
  if (kind === 'seal') return 1;
  let h = 0;
  // Its kind and slot: the same lane each day, and for the one come in its place.
  for (const c of kind.replace(/:\d+:(\d+):[01]$/, ':$1')) h = (h * 31 + c.charCodeAt(0)) % 1_000_003;
  return 2 + h;
}

/** The odds a honu, a hawksbill, a monk seal, a tiger shark (M197), a whitetip reef shark (M235) and a reef manta (M242) each visit a scene on a day. */
export interface VisitOdds {
  honu: number;
  honuea: number;
  seal: number;
  tigerShark: number;
  whitetip: number;
  manta: number;
}

/** A share of the days, as the panel says it: one day in so many, or none. */
export const daysIn = (p: number): string => (p >= 0.5 ? `${Math.round(p * 100)}% of days` : `about one day in ${Math.round(1 / p).toLocaleString('en-US')}`);

/**
 * How active a whitetip reef shark is at an hour (M235), 0 resting on the reef to 1 swimming it: by day "seen resting in
 * caves, sometimes for extended periods" (Hawaiʻi DLNR's shark identification guide), "nocturnally active, maintaining a
 * central location or core area to rest during the day, before dispersing at night" (Barnett et al. 2012, *PLoS ONE* 7:
 * e36574, CC BY, p. 10; 18 adults' acoustic tags at Osprey Reef, Coral Sea), and at one of its receivers, where they
 * spread at night, "peaking at dusk to early evening, between 18:00 and 21:00 h" (p. 7; at their core site, more by
 * day: review M235's); no Hawaiian timing read, Whitney et al. 2007 and 2012 refused here. Drawn swimming from
 * half an hour after sunset to the next sunrise, settling to rest over the half hour after it (JUDGED from the dusk
 * peak and the day's rest); `dayLengthHours` the day's light, sunrise and sunset about noon.
 */
export function whitetipActive(hour: number, dayLengthHours: number): number {
  if (dayLengthHours >= 24) return 0;
  if (dayLengthHours <= 0) return 1;
  const rise = 12 - dayLengthHours / 2;
  const set = 12 + dayLengthHours / 2;
  return 1 - windowShare(hour, rise, rise + 0.5) * (1 - windowShare(hour, set, set + 0.5));
}

/**
 * The hour a manta of the day's comes to the reef (M242), from a draw `u` in [0, 1): the share of the day's come by an
 * hour rising as Deakos's rays a survey's hour by the hour it began (`MANTA.sightingsPerHour`), none at 6:00 ("usually
 * absent at first light"), each bin's rate's share of the afternoon's by its middle hour (8:00, 12:00, 16:00), straight
 * between, every one come by 16:00 (JUDGED: the afternoon's surveys, 14:00 to 18:00, the most rays a day).
 */
export function mantaArrivalHour(u: number): number {
  const bins = MANTA.sightingsPerHour;
  const top = bins[bins.length - 1]?.rate ?? 1;
  const points: Array<[number, number]> = [[bins[0]?.fromHour ?? 6, 0], ...bins.map((b): [number, number] => [(b.fromHour + b.toHour) / 2, b.rate / top])];
  for (let i = 1; i < points.length; i++) {
    const [[h0, f0], [h1, f1]] = [points[i - 1] as [number, number], points[i] as [number, number]];
    if (u < f1) return h0 + ((h1 - h0) * (u - f0)) / (f1 - f0);
  }
  return (points[points.length - 1] as [number, number])[0];
}

/** Whether a manta come at `arriveHour` is on the reef at an hour (M242): from its coming to sunset, gone offshore after it (the two tagged, Deakos 2010, p. 72). */
const mantaHere = (hour: number, arriveHour: number, dayLengthHours: number): boolean => hour >= arriveHour && hour < 12 + dayLengthHours / 2;

/** A visitor whose kind is a humpback (M210). */
const isWhale = (kind: Visitor['kind']): boolean => kind === 'humpback';

/**
 * Each visitor's breadth from above over its length, its shadow's shape (M246; JUDGED, a drawing's soft blob): a turtle's
 * shell nearly as broad as long; a seal's and a shark's bodies narrow; a manta's wings about twice as broad as it is long
 * head to tail's root; any other fish a reef fish's.
 */
const VISITOR_BREADTH = { honu: 0.8, honuea: 0.75, seal: 0.25, tigerShark: 0.16, whitetip: 0.15, manta: 2.2, fish: 0.2 } as const;

/** The whale's frames' empty rows under its lowest drawn one, the least of its two, pixels (`WHALE_VISIT.floorGapM`). */
const WHALE_EMPTY_BELOW_PX = Math.min(...WHALE_SPRITES.whale.map((f) => f.height - 1 - f.rows.map((r) => /[^.]/.test(r)).lastIndexOf(true)));

/** A visitor whose kind is a sea turtle, the honu or the hawksbill (M196). */
const isTurtle = (kind: Visitor['kind']): kind is 'honu' | 'honuea' => kind === 'honu' || kind === 'honuea';

/** A visitor of one of the kinds a day's odds bring (M145, the hawksbill M196, the tiger shark M197, the whitetip M235, the manta M242), not a species' from its own habitat. */
const isOddsVisitor = (kind: Visitor['kind']): kind is keyof VisitOdds => isTurtle(kind) || kind === 'seal' || kind === 'tigerShark' || kind === 'whitetip' || kind === 'manta';

/** The panel's words for a scene's visitors: who came today, and how often each comes to these depths (M145; the hawksbill M196; the tiger shark M197; the whitetip M235). */
export function visitorsNote(visitors: readonly Visitor[], odds: VisitOdds): string {
  const names: Record<string, string> = { honu: sentenceName(speciesOf('honu')), honuea: sentenceName(speciesOf('honuea')), seal: sentenceName(speciesOf('monkSeal')), tigerShark: sentenceName(speciesOf('tigerShark')), whitetip: sentenceName(speciesOf('whitetip')), manta: sentenceName(speciesOf('manta')) };
  const today = visitors.filter((v) => isOddsVisitor(v.kind) && v.kind !== 'whitetip').map((v) => `a ${v.stage === 'young' ? 'young ' : v.stage === 'adult' ? '' : `${v.stage} `}${names[v.kind]}, ${v.lengthM.toFixed(1)} m${isTurtle(v.kind) ? ' its shell' : v.kind === 'manta' ? ` across, on the reef from ${clockLabel(v.arriveHour ?? 0)} to sunset` : ''}`);
  // A whitetip at home on its reef, not visiting (M235's review), and told only where it lives.
  const home = visitors.filter((v) => v.kind === 'whitetip').map((v) => `; at home here today: a${v.stage === 'adult' ? 'n adult' : ' young'} ${names.whitetip}, ${v.lengthM.toFixed(1)} m`).join('');
  const how = (['honu', 'honuea', 'seal', 'tigerShark', 'whitetip', 'manta'] as const).filter((k) => (k !== 'whitetip' && k !== 'manta') || odds[k] > 0).map((k) => (odds[k] > 0 ? `the ${names[k]} on ${daysIn(odds[k])}` : `the ${names[k]} never (no share of its time here measured)`));
  return `${today.length ? `visiting today: ${today.join(' and ')}` : 'no honu, hawksbill, monk seal or tiger shark visiting today'}${home}; each comes on visits of a few days, as often as its odds here: ${how.join(', ')}`;
}

/** Each visiting species' name as the panel gives it, in a sentence: the registry's (M160, `src/sim/species.ts`), one and more than one. */
const VISITOR_NAMES: Readonly<Record<VisitorSpecies, readonly [string, string]>> = Object.fromEntries(
  (Object.keys(SPECIES_VISITS) as VisitorSpecies[]).map((id) => [id, [sentenceName(speciesOf(id)), sentenceName(speciesOf(id), true)]]),
) as unknown as Record<VisitorSpecies, readonly [string, string]>;

/** A species' visit to a scene as the panel tells it (M147): how many come today, how many on the mean day, against its home's density, and whether it is drawn. */
export interface SpeciesVisitNote {
  species: VisitorSpecies;
  home: string;
  perAreaOfHome: number;
  /** Its mean count a day in the scene. */
  expected: number;
  count: number;
  /** Why it is not drawn, where it is not. */
  notDrawn: string | null;
}

/** The panel's words for the species visiting a scene from their own habitats (M147): today's, their mean day's, and their density here against home. */
export function speciesVisitsNote(visits: readonly SpeciesVisitNote[], homeNames: Readonly<Record<string, string>>): string {
  if (visits.length === 0) return '';
  const each = visits.map((v) => {
    const [one, many] = VISITOR_NAMES[v.species];
    const ratio = `${v.perAreaOfHome < 0.1 ? v.perAreaOfHome.toFixed(3) : v.perAreaOfHome.toFixed(2)} times as many a square meter as in ${homeNames[v.home] ?? v.home}`;
    const mean = v.expected >= 1 ? `${v.expected.toFixed(1)} on the mean day` : `one on ${daysIn(v.expected)}`;
    const now = v.count === 0 ? 'none today' : `${v.count} today`;
    return `${now === 'none today' ? `no ${many}` : `${v.count} ${v.count === 1 ? one : many}`} today (${mean}; ${ratio})${v.notDrawn ? `, not drawn: ${v.notDrawn}` : ''}`;
  });
  return `visiting from their own habitats, each one swapped once a day for another from home: ${each.join(', ')}`;
}

/**
 * The scene's place for its visitors: the home view's middle and the eye it is seen from (the lane runs across the
 * line between them, so a visitor is seen crossing the view, not coming at it; M145's review), and the bottom's height
 * under a point (none in open water).
 */
export interface VisitorWater {
  middle: THREE.Vector3;
  eye: THREE.Vector3;
  floorAt: ((x: number, z: number) => number) | null;
}

/**
 * The larger animals' use of the reef (M265, the interactions brief's section 7), JUDGED timings:
 * - The honu grazing: green turtles in Hawaiʻi feed on the reef's algae by day (Balazs 1980, NOAA TM NMFS-SWFC-7, not
 *   read here; the brief's own "Approach suitable algal feeding areas. Lower toward the reef. Graze briefly."): by day,
 *   at a mean `gapS` between bouts, a place of turf or limu within `withinM` of its lane taken, lowered to over
 *   `downS`, grazed `stayS` (its head bobbing `bobM` every `bobS`, tilted `tilt` nose down), risen over `upS`; on its lane
 *   the while held.
 * - The whitetip's rest: "seen resting in caves" by day (`whitetipActive`'s sources): at rest in the nearest shelter
 *   (an outcrop's foot, a coral's) within `shelterM` of where its lane leaves it, swum there and out as it settles and
 *   wakes.
 * The seals, the mantas, the tiger sharks and the whales keep to their own ways (the brief: "Do not force every species
 * to interact with the substrate").
 */
export const VISITOR_LIFE = { honu: { gapS: 25, withinM: 2, downS: 1.5, stayS: [4, 7], upS: 1.5, bobM: 0.03, bobS: 0.8, tilt: 0.2 }, shelterM: 6, restEaseS: 2 } as const;

/** The reef's places the visitors use (M265): food near a point, a shelter near a point; null for none. */
export interface VisitorReef {
  feedAt(x: number, z: number, withinM: number, u: number): { x: number; y: number; z: number } | null;
  shelterAt(x: number, z: number, withinM: number, u: number): { x: number; y: number; z: number } | null;
}

const SEAL_STAGES = ['adult', 'juvenile', 'pup'] as const;
const SHARK_STAGES = ['adult', 'juvenile'] as const;

/** A visitor as drawn: the day's visitor, its fading, its stroke, and where and which way it last went. */
interface Shown {
  visitor: Visitor;
  wanted: boolean;
  alpha: number;
  stroke: number;
  /** Its lane (`laneOf`), set as it is first drawn and kept through a hand-over (M203's second review: worked out each frame from what it stood in for, it jumped a median 3.2 m at a day's turn). */
  lane: number;
  /** Its place along its lane's round, 0 to 1, moved on at its own pace, so a new day's length changes its pace, not its place (M145's second review). */
  lap: number;
  /** Its height as drawn, eased toward the floor's under it (M150's review: an outcrop's edge popped it 0.41 m in a frame), null until first drawn. */
  y: number | null;
  lastAt: THREE.Vector3 | null;
  way: { x: number; z: number };
  right: boolean | null;
  /** Its middle and size as last drawn, m, for a tap (M152). */
  drawn: { middle: THREE.Vector3; width: number; height: number } | null;
  /** A whale's lane's distance from the home view's eye as drawn, m, eased toward the screen's own (M210's review: a panel opened moved it 7.5 m in a frame); null until first drawn and for the rest. */
  away: number | null;
  /** Its own seconds, its next graze's on them, and a graze under way: its seconds in and its place (a honu's, M265). */
  clock?: number;
  nextGraze?: number;
  graze?: { t: number; stay: number; place: { x: number; y: number; z: number } } | null;
  /** A whitetip's share at rest, eased (M265), its shelter for this rest, and a developer's rest until (its own seconds). */
  rest?: number;
  shelter?: { x: number; y: number; z: number } | null;
  restUntil?: number;
  /** Seconds since it was last wanted (M266: its leaving's allowance, `VISITOR_PASS.leaveS`). */
  leftS: number;
}

/** A visitor as drawn, for a tap and the card that follows it (M152): the day's visitor, its middle, its size, m, its fading. */
export interface VisitorDrawn {
  /** Its name in the scene (`visitorId`, or the one it was handed over to, M203). */
  id: string;
  visitor: Visitor;
  middle: THREE.Vector3;
  width: number;
  height: number;
  alpha: number;
}

/** A visitor as a scene reports it for its card (M152): as drawn, with a honu's, a hawksbill's, a seal's or a tiger shark's odds of coming there on a day, or a species' visit there (M147). */
export type VisitorAt = VisitorDrawn & { odds: number; visit: SpeciesVisitNote | null };

/** A drawn visitor for its card, with its kind's odds or its species' visit by the scene's day's data. */
export function visitorAtOf(d: VisitorDrawn, odds: VisitOdds | null | undefined, visits: readonly SpeciesVisitNote[] | undefined): VisitorAt {
  const k = d.visitor.kind;
  return { ...d, odds: isOddsVisitor(k) ? odds?.[k] ?? 0 : 0, visit: visits?.find((v) => v.species === k) ?? null };
}

/** Each habitat at Maui Nui as the panel and the card name it, a species' home among them (M147). */
export const HABITAT_NAMES: Readonly<Record<string, string>> = { reef: 'Maui Nui Reef', slope: 'the Slope', blue: 'the Blue Water', twilight: 'the Twilight Zone', deep: 'the Deep' };

/** A scene's visitors: their sprites in one batch, each on its lane across the view, each kind's its own. */
export class HabitatVisitors {
  readonly group = new THREE.Group();
  private readonly batch: FishBatch;
  private readonly rects = new Map<Sprite, [number, number, number, number]>();
  /** Each visitor by its own name (`visitorId`), so a new day's of the same kind keeps its lane and its fading (M145's review). */
  private readonly shown = new Map<string, Shown>();
  /** The visitors a hunt draws for now, by name (M251, the developer's trigger alone: the simulation gives none of them a reef fish to eat). */
  private readonly poses = new Map<string, FishPose>();
  private lastPosable: PosableFish[] = [];
  /** The day's visitors and the hour: a species' slot shows the one before its swap or the one after it (M147). */
  private day: Visitor[] = [];
  private hour = 0;
  /** The day's light, hours (a whitetip's rest, M235): the scene's, 12 until given. */
  private dayLengthHours = 12;
  /** Across the view and along it, on the level: the lane's way and its depth's. */
  private readonly across: THREE.Vector3;
  private readonly along: THREE.Vector3;
  private camera: THREE.Camera | null = null;
  /** The part of the screen the panels leave, in normalized device coordinates (`ReefScene.fit`'s; the whole until given). */
  private visible: readonly [number, number, number, number] = [-1, 1, -1, 1];
  /**
   * The whales' own batch (M210's review), drawn solid before the reef's animals (`renderOrder` under the particles'
   * 3.3, the octopuses' 3.5 and the fish's 4) and writing its depth at its middle's distance just after (M210's second
   * review: writing none, the fish beyond its lane were drawn over it, 40 of 47 on a phone on its side), so a fish, an
   * octopus or another visitor nearer the camera is drawn over the whale and one beyond it is hidden by it, as the
   * coral colonies hide what stands behind them (M164); its depth none while it is less than half seen, fading. Its
   * atlas and its uniforms the visitors' own.
   */
  private readonly whales: FishBatch;
  private readonly castersNow: Caster[] = [];
  /** The reef's places the visitors use (M265), none until given (`setReef`). */
  private reef: VisitorReef | null = null;

  constructor(private readonly water: VisitorWater) {
    const sprites = [...HONU_FRAMES.map((f) => f.sprite), ...HAWKSBILL_SPRITES.hawksbill, ...SEAL_STAGES.flatMap((s) => (MONK_SEAL_SPRITES[s] ? [MONK_SEAL_SPRITES[s] as Sprite] : [])), ...SHARK_STAGES.flatMap((s) => (TIGER_SHARK_SPRITES[s] ? [TIGER_SHARK_SPRITES[s] as Sprite] : [])), ...WHALE_SPRITES.whale, REEF_FISH_SPRITES.whitetip, REEF_FISH_SPRITES.manta, ...FISH_KINDS.flatMap((k) => (k.terminal ? [k.initial, k.terminal] : [k.initial]))];
    const { texture, rects } = fishAtlas(sprites);
    for (const [s, r] of rects) this.rects.set(s, r);
    this.batch = new FishBatch(texture, true);
    this.batch.mesh.renderOrder = 4;
    this.whales = new FishBatch(texture, true, true, true);
    this.whales.mesh.renderOrder = 3.25;
    const uniforms = (this.batch.mesh.material as THREE.ShaderMaterial).uniforms;
    (this.whales.mesh.material as THREE.ShaderMaterial).uniforms = uniforms;
    const depth = this.whales.depthPass as THREE.Mesh;
    (depth.material as THREE.ShaderMaterial).uniforms = uniforms;
    depth.renderOrder = 3.26;
    this.group.add(this.whales.mesh, depth, this.batch.mesh);
    const look = new THREE.Vector3(water.middle.x - water.eye.x, 0, water.middle.z - water.eye.z);
    this.along = look.lengthSq() > 1e-12 ? look.normalize() : new THREE.Vector3(0, 0, -1);
    this.across = new THREE.Vector3(-this.along.z, 0, this.along.x);
  }

  /**
   * The day's visitors: one there as the scene opens fading in at a point of its round of its own; one come later
   * swimming in from beyond the view's edge, and one gone swimming on out of it (M266; a whale's, and any while still,
   * fading).
   */
  set(visitors: Visitor[]): void {
    // The seconds between the day's relays (a hand-over's relay of the same day, sooner, not counted).
    if (this.sinceSet >= VISITOR_PASS.leastRelayS) this.relayS = this.sinceSet;
    this.sinceSet = 0;
    this.day = visitors;
    // A hand-over kept only while the day's state names the one handed to, as the same kind (M203's third review: kept past
    // it, the next day's untagged visitor of its kind was drawn and followed under the tag's name; the fourth: a quick-load's
    // world, its tag ids given again, named another kind so).
    for (const [from, to] of this.handed) {
      const named = visitors.find((v) => visitorId(v) === to);
      if (!named || named.kind !== this.shown.get(to)?.visitor.kind) this.handed.delete(from);
    }
    this.refresh();
  }

  /** The reef's places the visitors use from now (M265): the honu's food, the whitetip's shelter. */
  setReef(reef: VisitorReef | null): void {
    this.reef = reef;
  }

  /** How far a honu is down at its grazing, 0 swimming to 1 on the reef (`VISITOR_LIFE.honu`). */
  private static grazing(s: Shown): number {
    const g = s.graze;
    if (!g) return 0;
    const H = VISITOR_LIFE.honu;
    const u = g.t < H.downS ? g.t / H.downS : g.t > H.downS + g.stay ? 1 - (g.t - H.downS - g.stay) / H.upS : 1;
    const c = Math.min(1, Math.max(0, u));
    return c * c * (3 - 2 * c);
  }

  /** A whitetip's share at rest now: the hour's (`whitetipActive`), or a developer's rest, eased (M265); 0 for any other. */
  private restOf(s: Shown): number {
    return s.visitor.kind === 'whitetip' ? (s.rest ?? 1 - whitetipActive(this.hour, this.dayLengthHours)) : 0;
  }

  /**
   * The developer's (the interactions brief's section 13): `graze` a honu here grazes now, `rest` a whitetip here rests now
   * (for 12 s of its own); whether one could.
   */
  trigger(name: 'graze' | 'rest'): boolean {
    for (const s of this.shown.values()) {
      if (name === 'graze' && s.visitor.kind === 'honu' && !s.graze) {
        s.nextGraze = -Infinity;
        this.startGraze(s, true);
        return !!s.graze;
      }
      if (name === 'rest' && s.visitor.kind === 'whitetip') {
        s.restUntil = (s.clock ?? 0) + 12;
        return true;
      }
    }
    return false;
  }

  /** A honu's graze begun where it has a place near (`VISITOR_LIFE.honu`), by day or as the developer asks. */
  private startGraze(s: Shown, now = false): void {
    const H = VISITOR_LIFE.honu;
    const clock = s.clock ?? 0;
    const k = s.lane;
    if (!now && (s.nextGraze === undefined || clock < s.nextGraze)) {
      s.nextGraze ??= clock + H.gapS * hash3(k, 11, 265);
      return;
    }
    s.nextGraze = clock + H.gapS * -Math.log(1 - 0.999 * hash3(k, Math.floor(clock), 266));
    const lane = this.at(s, true);
    const place = this.reef?.feedAt(lane.x, lane.z, H.withinM, hash3(k, Math.floor(clock), 267)) ?? null;
    if (place) s.graze = { t: 0, stay: H.stayS[0] + (H.stayS[1] - H.stayS[0]) * hash3(k, Math.floor(clock), 268), place };
  }

  /** The hour: a species' visitor whose slot's swap it has passed gone home, fading out, and the one come in its place fading in (M147). */
  setHour(hour: number, dayLengthHours = this.dayLengthHours): void {
    if (hour === this.hour && dayLengthHours === this.dayLengthHours) return;
    this.dayLengthHours = dayLengthHours;
    this.hour = hour;
    this.refresh();
  }

  private refresh(): void {
    for (const s of this.shown.values()) s.wanted = false;
    for (const v of this.day) {
      if (v.slot && v.slot.after !== this.hour >= v.slot.hour) continue;
      // A manta on the reef from the hour it comes to sunset (M242).
      if (v.arriveHour !== undefined && !mantaHere(this.hour, v.arriveHour, this.dayLengthHours)) continue;
      // One handed over to a tagged one (M203) drawn under that one's name until the day's state says so too.
      const id = this.resolve(visitorId(v));
      const k = laneOf(id);
      let was = this.shown.get(id);
      // A tagged one standing in for the day's visitor of its kind takes over its place as drawn (M203's review).
      if (!was && v.from !== undefined && !this.day.some((d) => visitorId(d) === v.from) && this.shown.has(v.from)) {
        this.handOver(v.from, id);
        was = this.shown.get(id);
      }
      // One handed over keeps its tag's name until the day's state names it (M203's third review: the next frame's refresh
      // gave it back the day's untagged visitor, its card offering the tag again).
      // One on the same visit keeps its look all the visit (M266's review: its kind's population read afresh each day gave
      // the same honu another shell's length mid-visit).
      const sameVisit = !this.anotherWorld && was !== undefined && v.visit !== undefined && was.visitor.visit === v.visit && !was.visitor.tag && !v.tag;
      if (was) Object.assign(was, { visitor: sameVisit ? was.visitor : id !== visitorId(v) && was.visitor.tag ? { ...v, tag: was.visitor.tag } : v, wanted: true, leftS: 0 });
      else {
        // One come after the scene opened swims in from beyond the view's edge, from one side or the other by its visit
        // (M266: it had faded in in the open water); one there as the scene opens is where its round has it, faded in;
        // and so one held still (review 31: parked past the edge, never seen), a whitetip come to rest by day (review 31:
        // whole and resting at once), and one whose way in the camera sees (review 31: moved, it came in mid-screen).
        const resting = this.restingNow(v.kind);
        const swimsIn = this.opened && !this.calm && !resting && !isWhale(v.kind) && this.relayS > VISITOR_PASS.swimInS;
        const shown: Shown = { visitor: v, wanted: true, alpha: 1, stroke: isWhale(v.kind) ? 0 : 2 * hash3(k, 7, 145), lane: k, lap: 0, y: null, lastAt: null, way: { x: 1, z: 0 }, right: null, drawn: null, away: null, leftS: 0 };
        shown.lap = swimsIn ? this.enteringLap(v, hash3(k, v.visit ?? v.slot?.day ?? 9, 266) < 0.5) : 0;
        // Its way in moved out along its lane until none of it is seen (review 31's second round: a lane farther than the
        // view's middle left a sliver of it on screen at the home view's edge, and three kinds faded in mid-water every
        // visit); both ways in are left by a smaller lap, to its lane's end at 0 or 0.5. Faded in only where even the
        // lane's end is seen (the camera moved far out).
        if (swimsIn) {
          const end = shown.lap < 0.5 ? 0 : 0.5;
          while (this.seen(shown) && shown.lap > end) shown.lap = Math.max(end, shown.lap - VISITOR_PASS.entryStep);
        }
        if (!swimsIn || this.seen(shown)) Object.assign(shown, { alpha: 0, lap: hash3(k, v.slot?.after ? 4 : 2, 145) });
        this.shown.set(id, shown);
      }
    }
    for (const [from, to] of this.handed) if (!this.shown.has(to)) this.handed.delete(from);
    this.anotherWorld = false;
  }

  /** Another world laid on the scene (a game loaded, a new one): each visitor shown takes the new day's look (review 31's second round: kept on the same visit's day, the old game's). */
  newWorld(): void {
    this.anotherWorld = true;
  }

  private anotherWorld = false;

  /**
   * Whether the scene has been drawn (M266: any visitor after its first frame comes in from beyond the view; review 31:
   * set at the first set, one there as the scene opened by day, its hour not yet given, was taken for one come later).
   */
  private opened = false;
  /** Whether the last frame held still (a player asking for less motion). */
  private calm = false;
  /** The animation's seconds since the day's last relay, and between the last two (M266: a leaving's allowance). */
  private sinceSet = 0;
  private relayS: number = VISITOR_PASS.leaveS * 2;

  /**
   * Whether any of a visitor is seen in the part of the screen the panels leave: its middle or either end of it along the
   * screen (review 31: by its middle alone, one leaving was gone with half of it still on screen).
   */
  private seen(s: Shown): boolean {
    const c = this.camera;
    if (!c) return false;
    const at = this.at(s);
    const half = (s.drawn?.width ?? drawnLengthM(s.visitor)) / 2;
    const right = new THREE.Vector3().setFromMatrixColumn(c.matrixWorld, 0).setY(0).normalize();
    return [0, -1, 1].some((e) => inView(c, at.clone().addScaledVector(right, e * half), this.visible));
  }

  /**
   * How far a visitor's lane runs either side of the view's middle (M266): past the edge of what the home view sees
   * across there by `VISITOR_PASS.beyond` and its own length, so it turns about out of sight and is seen only passing
   * (the user's word: "the animal should not reverse direction repeatedly on an artificial visitor lane"); m.
   */
  private reachM(v: Visitor): number {
    return Math.max(this.spanM(v), this.seenM(v)) * VISITOR_PASS.beyond + drawnLengthM(v);
  }

  /** How far either side of the view's middle the home view sees across the lane, m. */
  private seenM(v: Visitor): number {
    const c = this.camera;
    return c instanceof THREE.PerspectiveCamera ? this.halfWideM(c) : this.spanM(v) / VISITOR_DRAW.seenShare;
  }

  /** As `seenM`, through a lens: at the home view's distance, so the screen's shape alone moves it (M150's review). */
  private halfWideM(c: THREE.PerspectiveCamera): number {
    return this.water.eye.distanceTo(this.water.middle) * Math.tan(((c.fov / 2) * Math.PI) / 180) * c.aspect;
  }

  /**
   * The point of its round a visitor come after the scene opened starts at (M266's review: from its lane's end a honu took
   * 8 s to swim into view, longer than a day at 1×): just beyond the view's edge, half its drawn length out, going in.
   */
  private enteringLap(v: Visitor, fromLeft: boolean): number {
    const reach = this.reachM(v);
    const edge = Math.min(reach, this.seenM(v) + drawnLengthM(v) / 2);
    const there = (reach - edge) / (2 * reach);
    // From the right its return leg's own point (review 31: `1 - there / 2`, the left edge going out).
    return fromLeft ? there / 2 : 0.5 + there / 2;
  }

  /** Whether a visitor of a kind is at rest now, held where it is (M266): a whitetip by day (`whitetipActive`). */
  private restingNow(kind: Visitor['kind']): boolean {
    return kind === 'whitetip' && whitetipActive(this.hour, this.dayLengthHours) < 0.5;
  }

  /** Whether a visitor swims on through the view and out of it (M266), or is held to its lane within it, turning in view and fading (a whale's, M210). */
  private static passes(v: Visitor): boolean {
    return !isWhale(v.kind);
  }

  /** Hand-overs, from a visitor's name to the tagged one's that took its place as drawn (M203). */
  private readonly handed = new Map<string, string>();

  /** A name as drawn now: the one it was handed over to, if any. */
  private resolve(id: string): string {
    let at = id;
    for (let k = 0; k < 4 && this.handed.has(at); k++) at = this.handed.get(at) as string;
    return at;
  }

  /**
   * One drawn handed over to another name, its place, its lane and its fading kept (M203: a visitor tagged as it is
   * followed, its drawing its tag's own from then, named so at once, `tag`; a tagged one come in the day's visitor's
   * place).
   */
  handOver(from: string, to: string, tag?: Visitor['tag']): void {
    const was = this.shown.get(from);
    if (!was || from === to) return;
    this.shown.delete(from);
    this.shown.set(to, tag ? { ...was, visitor: { ...was.visitor, tag } } : was);
    this.handed.set(from, to);
  }

  /** The sprite of a visitor, and its length in the sprite, pixels. */
  private spriteOf(v: Visitor, frame: number): { sprite: Sprite; lengthPx: number } {
    if (v.kind === 'honu') return { sprite: (HONU_FRAMES[frame] ?? HONU_FRAMES[0]).sprite, lengthPx: HONU_CARAPACE_PX };
    if (v.kind === 'honuea') return { sprite: HAWKSBILL_SPRITES.hawksbill[frame] ?? HAWKSBILL_SPRITES.hawksbill[0], lengthPx: HAWKSBILL_CARAPACE_PX };
    // The humpback's two frames, its length the sprite's width (`scripts/whaleSprites.ts`).
    if (isWhale(v.kind)) {
      const sprite = WHALE_SPRITES.whale[frame] ?? WHALE_SPRITES.whale[0];
      return { sprite, lengthPx: sprite.width };
    }
    const fish = fishKindOf(v.kind);
    if (fish) {
      const sprite = v.stage === 'terminal' && fish.terminal ? fish.terminal : fish.initial;
      return { sprite, lengthPx: sprite.width / VISITOR_DRAW.fishTimes };
    }
    // A whitetip's its one (M235, the user's: its young drawn by it too), its own length nose to tail.
    if (v.kind === 'whitetip') return { sprite: REEF_FISH_SPRITES.whitetip, lengthPx: REEF_FISH_SPRITES.whitetip.width };
    // A manta's its one (M242), its disc's width the sprite's: seen from three quarters, its wings spread across it.
    if (v.kind === 'manta') return { sprite: REEF_FISH_SPRITES.manta, lengthPx: REEF_FISH_SPRITES.manta.width };
    // A tiger shark's sprite its stage's (M197), drawn its own length nose to tail, as the seal's is.
    if (v.kind === 'tigerShark') {
      const sprite = (TIGER_SHARK_SPRITES[v.stage as 'adult' | 'juvenile'] ?? TIGER_SHARK_SPRITES.adult) as Sprite;
      return { sprite, lengthPx: sprite.width };
    }
    const sprite = (MONK_SEAL_SPRITES[v.stage as 'adult' | 'juvenile' | 'pup'] ?? MONK_SEAL_SPRITES.adult) as Sprite;
    return { sprite, lengthPx: sprite.width };
  }

  /**
   * The lane's half-length: `spanM`, or less where the home view sees less across at the lane, so a visitor stays in
   * view on a phone held upright (M145's second review: at `spanM` there it was on screen 30% of the time); read at the
   * home view's distance, the screen's shape alone moving it, so a zoom or a pan moves no visitor (M150's review: read
   * at the camera's own distance, a pinch moved a seal 1.65 m in a frame).
   */
  private spanM(v?: Visitor, away?: number | null): number {
    if (v && isWhale(v.kind)) return (away ?? this.whaleAwayM(v)) * this.halfWidePerM() + v.lengthM / 2;
    const c = this.camera;
    if (!(c instanceof THREE.PerspectiveCamera)) return VISITOR_DRAW.spanM;
    return Math.max(1, Math.min(VISITOR_DRAW.spanM, VISITOR_DRAW.seenShare * this.halfWideM(c)));
  }

  /**
   * Half what the part of the screen the panels leave sees across a meter from the eye, m: the camera's lens's (its
   * projection's, as the shader's `NEAR_FADE` reads it) times that part's share of the canvas's width (M210's review:
   * the canvas's whole, the whale was 1.25 times the gap between a computer's panels); 1 with no camera's lens yet.
   */
  private halfWidePerM(): number {
    const c = this.camera;
    return c instanceof THREE.PerspectiveCamera ? (this.visible[1] - this.visible[0]) / 2 / Math.max(1e-6, c.projectionMatrix.elements[0] as number) : 1;
  }

  /** How far a whale's lane lies from the home view's eye, m (`WHALE_VISIT`): its length `fitShare` of the screen's width there. */
  private whaleAwayM(v: Visitor): number {
    return Math.max(WHALE_VISIT.leastM, v.lengthM / (2 * WHALE_VISIT.fitShare * this.halfWidePerM()));
  }

  /**
   * The floor's highest within a whale's length either side of a point of its lane, at an eighth of its length apart
   * (M210): its flipper kept off the reef, an outcrop met half its length ahead of it so its height, eased over
   * `riseS` as the others' is, is over the rock before it is (a step under its body alone popped it 0.3 m in a tenth
   * of a second, MEASURED in `tests/humpbackReef.test.ts`'s first form).
   */
  private floorUnder(x: number, z: number, lengthM: number): number {
    const floorAt = this.water.floorAt as (x: number, z: number) => number;
    let top = -Infinity;
    for (let k = -8; k <= 8; k++) top = Math.max(top, floorAt(x + (this.across.x * k * lengthM) / 8, z + (this.across.z * k * lengthM) / 8));
    return top;
  }

  /** A visitor's pace, its lengths a second: a whale's the open water's (M210), a reef fish's `fishTimes` its own. */
  private static lengthsPerS(v: Visitor): number {
    return isWhale(v.kind) ? WHALE_DRAW.lengthsPerS : VISITOR_DRAW.lengthsPerS * (fishKindOf(v.kind) ? VISITOR_DRAW.fishTimes : 1);
  }

  /** A visitor on its lane at its place in its round: its middle, the lane its own, across the view. */
  private at(shown: Shown, laneOnly = false): THREE.Vector3 {
    const v = shown.visitor;
    const k = shown.lane;
    const whale = isWhale(v.kind);
    // A whale's lane its own distance from the eye (M210), the others' about the home view's middle.
    const e = this.water.eye;
    const away = shown.away ?? this.whaleAwayM(v);
    // A whale's lane its own of the day's two or more, within 0.15 of its length nearer or farther (M210's review: two
    // drawn on one line passed through each other), never nearer than `leastM` (M210's second review: put on after it,
    // a lane stood 6.1 m off).
    const spread = (hash3(k, 1, 145) - 0.5) * (whale ? v.lengthM * WHALE_VISIT.laneShare : VISITOR_DRAW.spanM * 0.6);
    const off = Math.max(WHALE_VISIT.leastM, away + spread);
    const m = whale ? new THREE.Vector3(e.x + this.along.x * off, this.water.middle.y, e.z + this.along.z * off) : this.water.middle;
    const span = this.spanM(v, shown.away);
    const depth = whale ? 0 : spread;
    // A whale back and forth within the view, turning eased at its ends; any other across it and past its edges at an
    // even pace, turning about out of sight (M266).
    const reach = HabitatVisitors.passes(v) ? this.reachM(v) : span;
    const there = shown.lap < 0.5 ? 2 * shown.lap : 2 - 2 * shown.lap;
    const s = HabitatVisitors.passes(v) ? -reach + 2 * reach * there : -span + 2 * span * ((1 - Math.cos(2 * Math.PI * shown.lap)) / 2);
    let x = m.x + this.across.x * s + this.along.x * depth;
    let z = m.z + this.across.z * s + this.along.z * depth;
    // A honu down at its grazing, a whitetip in its shelter at rest (M265): moved as far toward it as it is there.
    const graze = laneOnly ? 0 : HabitatVisitors.grazing(shown);
    if (graze > 0 && shown.graze) {
      const H = VISITOR_LIFE.honu;
      const bob = H.bobM * Math.sin((2 * Math.PI * shown.graze.t) / H.bobS);
      x += (shown.graze.place.x - x) * graze + this.across.x * bob * graze;
      z += (shown.graze.place.z - z) * graze + this.across.z * bob * graze;
    }
    const resting = laneOnly ? 0 : this.restOf(shown);
    if (resting > 0 && shown.shelter) {
      x += (shown.shelter.x - x) * resting;
      z += (shown.shelter.z - z) * resting;
    }
    if (whale) {
      const sprite = WHALE_SPRITES.whale[0];
      const height = (v.lengthM * sprite.height) / sprite.width;
      const low = this.water.floorAt ? this.floorUnder(x, z, v.lengthM) + WHALE_VISIT.floorGapM - (WHALE_EMPTY_BELOW_PX / sprite.height) * height + height / 2 : m.y;
      return new THREE.Vector3(x, Math.min(low, -WHALE_VISIT.surfaceM - height / 2), z);
    }
    const drawnM = drawnLengthM(v);
    // A whitetip resting lies on the floor, its middle half its sprite's height over it, its foot on it (M235's review: its
    // whole height, it hung half a body over the floor); swimming, as the others.
    const active = v.kind === 'whitetip' ? 1 - this.restOf(shown) : 1;
    const lying = v.kind === 'whitetip' ? (drawnM * REEF_FISH_SPRITES.whitetip.height) / REEF_FISH_SPRITES.whitetip.width : 0;
    let y = this.water.floorAt ? this.water.floorAt(x, z) + active * drawnM * (0.5 + VISITOR_DRAW.aboveShare) + ((1 - active) * lying) / 2 : m.y + (hash3(k, 3, 145) - 0.5) * 4;
    // Down to its grazing: its middle its carapace's height over the reef there.
    if (graze > 0 && shown.graze) y += (shown.graze.place.y + 0.35 * v.lengthM - y) * graze;
    return new THREE.Vector3(x, y, z);
  }

  /** Moved on by `seconds`, each faded toward being there or gone; still within itself when `calm`. */
  swim(seconds: number, calm = false): void {
    if (Number.isFinite(seconds)) this.sinceSet += Math.max(0, seconds);
    [this.opened, this.calm] = [true, calm];
    const allowS = Math.min(VISITOR_PASS.leaveS, this.relayS / 2);
    for (const [kind, s] of this.shown) {
      // One gone swims on out of view, within its allowance, before it leaves the drawing (M266: it had faded out in the
      // open water as the day turned); past it, a whale, one held still and one resting fades where it is, as before.
      if (!s.wanted && Number.isFinite(seconds)) s.leftS += Math.max(0, seconds);
      const resting = this.restingNow(s.visitor.kind);
      const swimsOut = !s.wanted && HabitatVisitors.passes(s.visitor) && !calm && !resting && s.leftS < allowS;
      if (swimsOut && this.camera && s.y !== null && !this.seen(s)) {
        this.shown.delete(kind);
        continue;
      }
      if (!swimsOut) s.alpha = s.wanted ? Math.min(1, s.alpha + seconds / VISITOR_DRAW.fadeS) : s.alpha - seconds / Math.min(VISITOR_DRAW.fadeS, Math.max(allowS, VISITOR_PASS.leastFadeS));
      if (s.alpha <= 0 && !s.wanted) {
        this.shown.delete(kind);
        continue;
      }
      const lapBefore = s.lap;
      // A whale's lane's distance eased toward the screen's own over `easeS`.
      if (isWhale(s.visitor.kind)) {
        const want = this.whaleAwayM(s.visitor);
        s.away = s.away === null ? want : s.away + (want - s.away) * Math.min(1, seconds / WHALE_VISIT.easeS);
      }
      // A whitetip still where it rests by day (M235), on its lane as it swims by night.
      // Its own clock; a honu's grazing by day, a whitetip's rest eased toward the hour's (or a developer's), its shelter
      // found as it settles (M265).
      s.clock = (s.clock ?? 0) + seconds;
      if (s.visitor.kind === 'honu' && !calm && this.reef) {
        if (s.graze) {
          s.graze.t += seconds;
          if (s.graze.t > VISITOR_LIFE.honu.downS + s.graze.stay + VISITOR_LIFE.honu.upS) s.graze = null;
        } else if (this.dayLengthHours > 0 && Math.abs(this.hour - 12) < this.dayLengthHours / 2) this.startGraze(s);
      }
      if (s.visitor.kind === 'whitetip') {
        const want = (s.restUntil ?? -Infinity) > s.clock ? 1 : 1 - whitetipActive(this.hour, this.dayLengthHours);
        s.rest = s.rest === undefined ? want : s.rest + Math.max(-seconds / VISITOR_LIFE.restEaseS, Math.min(seconds / VISITOR_LIFE.restEaseS, want - s.rest));
        if (s.rest > 0.02 && !s.shelter && this.reef) {
          // Looked for within the view (review M267's: since M266 a passing visitor's lane runs past the view's edge, and a
          // whitetip settling where dawn found it rested out of sight all day, 18 dawns in 40 on a phone, MEASURED): its
          // lane's point brought within half the view's half-width of its middle, a shelter sought no farther off than
          // that, or failing one it rests on the floor there.
          const lane = this.at(s, true);
          const half = 0.5 * this.seenM(s.visitor);
          const m = this.water.middle;
          const t = (lane.x - m.x) * this.across.x + (lane.z - m.z) * this.across.z;
          const kept = Math.max(-half, Math.min(half, t));
          const [x, z] = [lane.x + this.across.x * (kept - t), lane.z + this.across.z * (kept - t)];
          // No farther off than keeps it within that half again (review M267's third round: a shelter sought half the view's
          // half-width from a point already there lay at the view's very edge).
          const reach = Math.min(VISITOR_LIFE.shelterM, half - Math.abs(kept));
          s.shelter = this.reef.shelterAt(x, z, reach, hash3(s.lane, 9, 265)) ?? { x, y: this.water.floorAt?.(x, z) ?? lane.y, z };
        } else if (s.rest <= 0.02) s.shelter = null;
      }
      const active = s.visitor.kind === 'whitetip' ? 1 - this.restOf(s) : 1 - HabitatVisitors.grazing(s);
      const round = 4 * (HabitatVisitors.passes(s.visitor) ? this.reachM(s.visitor) : this.spanM(s.visitor, s.away));
      if (!calm) s.lap = (s.lap + (active * seconds) / (round / (HabitatVisitors.lengthsPerS(s.visitor) * s.visitor.lengthM))) % 1;
      // A whale held still in the middle of its lane, across the view, its way kept (M210's second review: held where its
      // lap had it, it was cut by an edge for its whole slot, its lane running past the view's edges).
      else if (isWhale(s.visitor.kind)) s.lap = s.lap < 0.5 ? 0.25 : 0.75;
      // Gone once it reaches an end of its lane, beyond the view's edge.
      if (swimsOut && (s.lap < lapBefore || (lapBefore < 0.5 && s.lap >= 0.5))) {
        this.shown.delete(kind);
        continue;
      }
      // Its height eased toward the floor's under its new place, a whole change over `riseS`.
      if (!calm) {
        const want = this.at(s).y;
        s.y = s.y === null ? want : s.y + (want - s.y) * Math.min(1, seconds / VISITOR_DRAW.riseS);
      }
      if (s.visitor.kind === 'honu' && !calm) s.stroke = (s.stroke + seconds / strokeHoldS((VISITOR_DRAW.lengthsPerS * HONU_CARAPACE_PX) / HONU_STROKE.bodyPx)) % 2;
      if (s.visitor.kind === 'honuea' && !calm) s.stroke = (s.stroke + seconds / strokeHoldS((VISITOR_DRAW.lengthsPerS * HAWKSBILL_CARAPACE_PX) / HAWKSBILL_STROKE.bodyPx)) % 2;
      // A whale's seconds since it came, its frames the open water's by them (`strokeFrame`, M210).
      if (isWhale(s.visitor.kind) && !calm) s.stroke += seconds;
    }
    this.draw();
  }

  /** Each turned the way `camera` sees it swim (M142); a whale fitted to `visible`, the part of the screen the panels leave (M210). */
  face(camera: THREE.Camera, visible?: readonly [number, number, number, number]): void {
    this.camera = camera;
    if (visible) this.visible = visible;
    this.draw();
  }

  private draw(): void {
    const draws: FishDraw[] = [];
    const whales: FishDraw[] = [];
    this.castersNow.length = 0;
    const posable: PosableFish[] = [];
    const axes = this.camera ? { right: new THREE.Vector3().setFromMatrixColumn(this.camera.matrixWorld, 0).normalize(), up: new THREE.Vector3().setFromMatrixColumn(this.camera.matrixWorld, 1).normalize() } : undefined;
    for (const s of this.shown.values()) {
      const v = s.visitor;
      const at = this.at(s);
      if (s.y !== null) at.y = s.y;
      if (s.lastAt && Math.hypot(at.x - s.lastAt.x, at.z - s.lastAt.z) > 1e-9) s.way = { x: at.x - s.lastAt.x, z: at.z - s.lastAt.z };
      const right = facesRight(this.camera, at, s.way, s.right ?? s.way.x > 0);
      [s.lastAt, s.right] = [at, right];
      const frame = isWhale(v.kind) ? strokeFrame(s.stroke, strokeStart(s.lane)) : isTurtle(v.kind) ? Math.floor(s.stroke) % 2 : 0;
      const { sprite, lengthPx } = this.spriteOf(v, frame);
      const alpha = Math.max(0, s.alpha);
      // Its shadow's caster (M246): its middle, its drawn length (a reef fish's `fishTimes` its own, M256), its kind's
      // breadth from above, the way it swims; a whale none, in the open water far over the reef. A manta's own measure is
      // across its wings, its sprite drawn that wide across the screen: its shadow's length its wings, laid along the
      // screen as every swimmer's is (M263), its body a share of them across (review M263's: its wings had pointed into
      // the screen).
      const breadth = VISITOR_BREADTH[v.kind as keyof typeof VISITOR_BREADTH] ?? VISITOR_BREADTH.fish;
      const length = drawnLengthM(v);
      const caster = { x: at.x, y: at.y, z: at.z, length, width: v.kind === 'manta' ? 1 / breadth : breadth, headX: s.way.x, headZ: s.way.z, alpha };
      if (!isWhale(v.kind)) this.castersNow.push(caster);
      if (v.kind === 'honu') {
        const width = (v.lengthM * HONU_SPRITE.width) / HONU_CARAPACE_PX;
        const q = frameQuad(frame, { width, height: (width * HONU_SPRITE.height) / HONU_SPRITE.width }, at, right, axes);
        s.drawn = { middle: at.clone(), width, height: (width * HONU_SPRITE.height) / HONU_SPRITE.width };
        // Its nose bent to the reef while it grazes (M265).
        const tilt = VISITOR_LIFE.honu.tilt * HabitatVisitors.grazing(s);
        draws.push({ ...q, rect: this.rects.get(sprite) as [number, number, number, number], right, alpha, ...(tilt > 0 ? { tilt } : {}) });
        continue;
      }
      const width = (v.lengthM * sprite.width) / lengthPx;
      const height = (width * sprite.height) / sprite.width;
      s.drawn = { middle: at.clone(), width, height };
      if (isWhale(v.kind)) {
        whales.push({ x: at.x, y: at.y - height / 2, z: at.z, width, height, rect: this.rects.get(sprite) as [number, number, number, number], right, alpha });
        continue;
      }
      const id = visitorId(v);
      const foot = new THREE.Vector3(at.x, at.y - height / 2, at.z);
      posable.push({ id, species: v.kind, kind: -1, phase: 0, at: foot, width, height, right, shown: alpha, sprite, school: -1, tlMm: v.lengthM * 1000 });
      // A hunt's pose where it has one (M251), drawn as the fish's are (`FishSchools.fit`).
      const pose = this.poses.get(id);
      const b = pose ? Math.min(1, Math.max(0, pose.blend)) : 0;
      const turned = pose?.right !== undefined && b > 0.5 ? pose.right : right;
      if (pose?.right !== undefined && b > 0.5) s.right = pose.right;
      const size = pose?.scale ?? 1;
      const drawn: FishDraw = {
        x: foot.x + ((pose?.at.x ?? foot.x) - foot.x) * b, y: foot.y + ((pose?.at.y ?? foot.y) - foot.y) * b, z: foot.z + ((pose?.at.z ?? foot.z) - foot.z) * b,
        width: width * size * (pose?.scaleX ?? 1), height: height * size, rect: this.rects.get(sprite) as [number, number, number, number], right: turned, alpha: alpha * (pose?.alpha ?? 1),
        ...(pose?.flash ? { flash: pose.flash } : {}),
      };
      draws.push(drawn);
      // Its shadow cast where a hunt's pose draws it, as large and as faded (M256, review M253's: it had cast from its lane).
      if (pose) [caster.x, caster.y, caster.z, caster.length, caster.alpha] = [drawn.x, drawn.y + drawn.height / 2, drawn.z, length * size * (pose.scaleX ?? 1), drawn.alpha];
    }
    this.lastPosable = posable;
    this.batch.set(draws);
    this.whales.set(whales);
  }

  /** The visitors drawn now as their shadows' casters (M246). */
  casters(): readonly Caster[] {
    return this.castersNow;
  }

  /** The visitors (not the whales, nor the honu, drawn by their strokes' frames) as last drawn, for a hunt (M251). */
  posable(): readonly PosableFish[] {
    return this.lastPosable;
  }

  /** A visitor drawn by a hunt's pose from now (M251), or given back to its own swimming (null). */
  pose(id: string, pose: FishPose | null): void {
    if (pose) this.poses.set(id, pose);
    else this.poses.delete(id);
  }

  /** The visitors as drawn now (M152). */
  drawnNow(): VisitorDrawn[] {
    return [...this.shown].flatMap(([id, s]) => (s.drawn ? [{ id, visitor: s.visitor, ...s.drawn, alpha: Math.max(0, s.alpha) }] : []));
  }

  /**
   * The visitor under a point on the screen, by its name (`visitorId`); null for none (M152, the user's word: "i cant tap
   * and select some sea creatures like monk seal and honu in habitat view").
   */
  pick(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): string | null {
    // Any other before a whale, drawn over it where nearer (M210's second review: the whale's box covering nearly any tap
    // on it, its middle took a tap on a seal's head drawn over it).
    const drawn = this.drawnNow();
    const d = pickNearest(drawn.filter((x) => !isWhale(x.visitor.kind)), camera, x, y, widthPx, heightPx, withinPx) ?? pickNearest(drawn.filter((x) => isWhale(x.visitor.kind)), camera, x, y, widthPx, heightPx, withinPx);
    return d ? d.id : null;
  }

  /** A visitor by its name as drawn now, or null where none is. */
  drawnOf(id: string): VisitorDrawn | null {
    const at = this.resolve(id);
    return this.drawnNow().find((d) => d.id === at) ?? null;
  }

  /**
   * A species' visitor of the day by its name gone home at its slot's swap, the hour past it (M147); null for any other,
   * the one come in its place not yet arrived among them (M147's review: an hour turned back had it gone home).
   */
  goneOf(id: string): Visitor | null {
    const v = this.day.find((d) => visitorId(d) === id);
    return v?.slot && !v.slot.after && this.hour >= v.slot.hour ? v : null;
  }

  /** The water they are seen through and the light they are seen by, the scene's (the whales' batch shares them). */
  fogUniforms(): Partial<FogUniforms> {
    return this.batch.fogUniforms();
  }

  setLight(r: number, g: number, b: number): void {
    this.batch.setLight(r, g, b);
  }

  /** What the batches draw, for the tests: the whales' after the others'. */
  draws(): FishDraw[] {
    return [...this.batch.drawn(), ...this.whales.drawn()];
  }

  /** The whales' batch's place in the drawing order, for the tests. */
  whalesOrder(): number {
    return this.whales.mesh.renderOrder;
  }

  dispose(): void {
    this.whales.dispose(false);
    this.batch.dispose();
  }
}
